import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { assembleAllFilterFields } from "./filterFieldBuilders";
import buildingActions from "../building/actions";
import recommendationActions from "../recommendations/actions";
import { getAllBuildingsIds } from "../building/services";

// Shared assembly of Otto's COMPLETE filter-field set (base + recommendation-dynamic + building-dynamic +
// priority bands) with the scope-stamped band loader, the readiness gate, and the send-time availability
// snapshot. Extracted from AiFilterAssistant so EVERY Otto mount — the dashboard AND the Recommendations
// page — offers and applies the EXACT same fields (all bands), with no drift. Dashboard-specific bits
// (navTargets, chartContext, executors) stay in the mount containers; this hook is filter-fields only.

// Max dynamic-band load attempts (initial + immediate retries) per scope key.
const BAND_MAX_ATTEMPTS = 3;
// Readiness escape hatch: how long one not-ready EPISODE may hold the gate before Otto proceeds degraded.
// Re-armed per episode (a scope change starts a fresh window) — it must never latch for the mount's life.
const BAND_READY_TIMEOUT_MS = 30000;

// One dynamic band, loaded by Otto ITSELF and STAMPED with the scope key it was fetched for. Owning a
// stamped copy (the thunk's RETURN value, not the shared reducer slot) means redux writes by other pages
// can neither poison nor wipe it, and a scope change simply reloads for the new key. `fetcher(key)` resolves
// to the band payload on success, null/undefined on failure. Status: "na" (not applicable — nothing to
// await), "pending" (fetch/retries in flight), "ready" (stamped payload for `key`), "failed" (attempts
// exhausted; terminal — the gate opens degraded and the availability span records it).
const useStampedBand = (key, fetcher) => {
    const [slot, setSlot] = useState({ key: null, status: "na", data: null, attempts: 0 });
    useEffect(() => {
        if (key == null || key === "") {
            setSlot({ key: null, status: "na", data: null, attempts: 0 });
            return undefined;
        }
        let cancelled = false;
        setSlot({ key, status: "pending", data: null, attempts: 0 });
        (async () => {
            for (let attempt = 1; attempt <= BAND_MAX_ATTEMPTS; attempt++) {
                let payload = null;
                try {
                    payload = await fetcher(key);
                } catch (e) {
                    payload = null; // best-effort: a throwing fetch is just a failed attempt
                }
                if (cancelled) return;
                if (payload) {
                    setSlot({ key, status: "ready", data: payload, attempts: attempt });
                    return;
                }
                setSlot({ key, status: attempt < BAND_MAX_ATTEMPTS ? "pending" : "failed", data: null, attempts: attempt });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [key, fetcher]);
    return slot;
};

// `getCachedProject` is passed IN (not imported) so the hook stays free of the dashboard session util's
// import graph for consumers that don't need the project-name/breadcrumb bits. Optional.
export function useOttoFilterFields({ projectId, projectIds, getCachedProject } = {}) {
    const dispatch = useDispatch();
    const baseFilterFields = useSelector(s => s.dashboardReducer?.filterFields);
    const reduxClientId = useSelector(s => s.projectReducer?.getProjectByIdResponse?.client?.id);
    const projectById = useSelector(s => s.projectReducer?.getProjectByIdResponse);
    const singleProjectId = projectId != null ? projectId : projectIds && projectIds.length === 1 ? projectIds[0] : undefined;

    const loadedRef = useRef({ buildingScope: null });
    // D2 per-user permission scope: the building ids THIS user can actually see (Rails policy_scope). Otto's
    // catalog is scoped to these. Best-effort — on failure/empty it stays null and Otto uses full project vocab.
    const [buildingIds, setBuildingIds] = useState(null);

    // The scope each band belongs to. `clientId` is derived scope-SAFELY: the loaded project's client only
    // counts when that project IS the current scope, else the session cache of THIS project, else redux.
    const cachedFor = useCallback(
        id => (typeof getCachedProject === "function" && id != null ? getCachedProject(id) : null),
        [getCachedProject]
    );
    const clientId = useMemo(() => {
        const matches = String(projectById?.id) === String(singleProjectId);
        if (matches && projectById?.client?.id != null) return projectById.client.id;
        const cached = singleProjectId != null ? cachedFor(singleProjectId) : null;
        if (cached?.client?.id != null) return cached.client.id;
        return reduxClientId != null ? reduxClientId : null;
    }, [projectById, singleProjectId, reduxClientId, cachedFor]);

    // Otto-owned, scope-stamped copies of the three dynamic bands. Priority is per single project; the two
    // custom-field bands are per client (recommendation-custom also accepts a project scope).
    const fetchPriorityBand = useCallback(
        async key => {
            const resp = await dispatch(recommendationActions.getPriorityElementDropDownData(key));
            return resp && resp.success ? resp : null;
        },
        [dispatch]
    );
    const fetchRecBand = useCallback(
        async key => {
            const [client, project] = String(key).split("::");
            const resp = await dispatch(
                recommendationActions.getDynamicFields({
                    ...(client ? { client_id: client } : {}),
                    ...(project ? { project_id: project } : {})
                })
            );
            return resp && resp.success ? resp : null;
        },
        [dispatch]
    );
    const fetchBuildingBand = useCallback(
        async key => {
            const resp = await dispatch(buildingActions.getBuildingDynamicFields({ client_id: key }));
            return resp && resp.success ? resp : null;
        },
        [dispatch]
    );
    const priorityBand = useStampedBand(singleProjectId != null ? String(singleProjectId) : null, fetchPriorityBand);
    const recKey =
        clientId != null || singleProjectId != null
            ? `${clientId != null ? clientId : ""}::${singleProjectId != null ? singleProjectId : ""}`
            : null;
    const recBand = useStampedBand(recKey, fetchRecBand);
    const buildingBand = useStampedBand(clientId != null ? String(clientId) : null, fetchBuildingBand);

    // Fetch the user's accessible building ids (Rails policy_scope) once per project scope.
    useEffect(() => {
        const ids = projectIds && projectIds.length ? projectIds : singleProjectId != null ? [singleProjectId] : [];
        if (!ids.length) return undefined;
        const key = [...ids].sort().join(",");
        if (loadedRef.current.buildingScope === key) return undefined;
        loadedRef.current.buildingScope = key;
        let active = true;
        Promise.resolve(getAllBuildingsIds({ project_ids: ids }, true))
            .then(res => {
                const list = res && res.data && res.data.building_ids;
                if (active && Array.isArray(list) && list.length) setBuildingIds(list);
            })
            .catch(() => {
                /* best-effort: leave buildingIds null -> Otto uses full project vocabulary */
            });
        return () => {
            active = false;
        };
    }, [projectIds, singleProjectId]);

    // Assemble ONLY from Otto's stamped copies — never the shared redux slots (a foreign band would surface
    // fields this project may not have; grounding boundary). Degraded (failed/timed-out) = genuinely absent.
    const filterFields = useMemo(
        () =>
            assembleAllFilterFields({
                baseFilterFields,
                recommendation_custom_fields: recBand.data?.recommendation_custom_fields,
                building_custom_field: buildingBand.data?.building_custom_field,
                priorityElementsDropDownResponse: priorityBand.data
            }),
        [baseFilterFields, recBand.data, buildingBand.data, priorityBand.data]
    );

    // Readiness gate: only "pending" holds it; "ready"/"na"/"failed" are terminal. A query typed before ready
    // is queued by FilterAssistant and replayed once ready. The 30s escape hatch covers a HUNG fetch and
    // re-arms per not-ready episode.
    const bandsSettled = [priorityBand, recBand, buildingBand].every(b => b.status !== "pending");
    const [loadTimedOut, setLoadTimedOut] = useState(false);
    useEffect(() => {
        if (bandsSettled) {
            setLoadTimedOut(false);
            return undefined;
        }
        const id = setTimeout(() => setLoadTimedOut(true), BAND_READY_TIMEOUT_MS);
        return () => clearTimeout(id);
    }, [bandsSettled]);
    const filtersReady = bandsSettled || loadTimedOut;

    // Send-time availability snapshot (the `fields.availability` client span) — which band was missing, for
    // which key, after how many attempts.
    const fieldsMeta = useMemo(() => {
        const bandMeta = slot => ({
            status: slot.status,
            ...(slot.key != null ? { key: String(slot.key) } : {}),
            ...(slot.attempts ? { attempts: slot.attempts } : {})
        });
        const bands = { priority: bandMeta(priorityBand), rec: bandMeta(recBand), building: bandMeta(buildingBand) };
        return {
            project_ids: projectIds ? projectIds.length : projectId != null ? 1 : 0,
            single_project_id: singleProjectId != null ? String(singleProjectId) : null,
            client_id: clientId != null ? String(clientId) : null,
            bands,
            timed_out: loadTimedOut,
            degraded: loadTimedOut || Object.values(bands).some(b => b.status === "failed" || b.status === "pending")
        };
    }, [priorityBand, recBand, buildingBand, projectIds, projectId, singleProjectId, clientId, loadTimedOut]);

    return { filterFields, filtersReady, fieldsMeta, buildingIds, singleProjectId, clientId, projectById };
}
